import { useSession } from '@/shared/hooks/useSession'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  H2,
  localAddressSchema
} from '@/common'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OrganizationAddressForm from './components/OrganizationAddressForm'
import {
  OrganizationAddress,
  organizationAddressSchema,
  OrganizationContact,
  organizationContactSchema,
  OrganizationLocalAddress
} from '@/features/organization/api/OrganizationModel'
import OrganizationContactForm from './components/OrganizationContactForm'
import {
  useDoesOrganizationRequiresLocalAddress,
  useUpdateOrganization
} from '@/shared/hooks/api/organizations'
import toast from 'react-hot-toast'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import { InfoFilledIcon, MapPinIcon, PencilIcon } from '@/common/components/icons'
import OrganizationInfoData from './components/OrganizationInfoData'
import { useDialog } from '@/shared/hooks/useDialog'
import UpdateBusinessCertificateDialog from './dialogs/UpdateBusinessCertificateDialog'
import OrganizationWithLocalAddressForm from './components/OrganizationWithLocalAddressForm'
import { Container } from '@/shared/components'

export default function OrganizationDetails() {
  const { t } = useTranslation()
  const { session } = useSession()
  const { mutateAsync: updateOrganization, isPending: isSaving } = useUpdateOrganization(
    session.organization.id
  )
  const {
    session: { organization }
  } = useSession()
  const {
    open: isOpenUpdateBusinessCertificateDialog,
    openDialog: openUpdateBusinessCertificateDialog,
    closeDialog: closeUpdateBusinessCertificateDialog
  } = useDialog()
  const { downloadDocument } = useOrganizationDocuments()
  const doesOrganizationRequiresLocalAddress = useDoesOrganizationRequiresLocalAddress(
    organization.placeOfRegistration
  )

  const [state, setState] = useState({
    street: organization.street ?? '',
    streetNumber: organization.streetNumber ?? '',
    country: organization.country ?? organization.placeOfRegistration,
    poBox: organization.poBox ?? '',
    city: organization.city ?? '',
    subDivision: organization.subDivision ?? '',
    street2: organization.street2 ?? '',
    postCode: organization.postCode ?? '',
    localStreet: organization.localStreet ?? '',
    localStreetNumber: organization.localStreetNumber ?? '',
    localSubDivision: organization.localSubDivision ?? '',
    localPostCode: organization.localPostCode ?? '',
    localPoBox: organization.localPoBox ?? '',
    localCity: organization.localCity ?? '',
    localStreet2: organization.localStreet2 ?? '',
    contactName: organization.contactName ?? '',
    contactEmail: organization.contactEmail ?? '',
    contactPhoneNumber: organization.contactPhoneNumber ?? ''
  })

  const [isFormPristine, setIsFormPristine] = useState(true)

  const onAddressChange = useCallback((address: OrganizationAddress) => {
    setState(prevState => ({
      ...prevState,
      ...address,
      localStreet: address.street ?? '',
      localStreetNumber: address.streetNumber ?? '',
      localSubDivision: address.subDivision ?? '',
      localPoBox: address.poBox ?? '',
      localCity: address.city ?? '',
      localPostCode: address.postCode ?? '',
      localStreet2: address.street2 ?? ''
    }))
    setIsFormPristine(false)
  }, [])

  const onAddressWithLocalChange = useCallback(
    (address: OrganizationAddress & OrganizationLocalAddress) => {
      setState(prevState => ({
        ...prevState,
        ...address
      }))
      setIsFormPristine(false)
    },
    []
  )

  const onContactChange = useCallback((contact: OrganizationContact) => {
    setState(prevState => ({
      ...prevState,
      ...contact
    }))
    setIsFormPristine(false)
  }, [])

  const onSaveChanges = useCallback(async () => {
    try {
      await updateOrganization(state)
      toast.success(t('settings:organizationDetails.notifications.saveChanges'))
      setIsFormPristine(true)
    } catch (error) {
      console.error('Error updating contact details:', error)
      toast.error(t('settings:organizationDetails.notifications.saveChangesError'))
    }
  }, [state, updateOrganization, t])

  const onClickCertificate = useCallback(async () => {
    if (session.organization.certificateFileName) {
      const result = await downloadDocument(session.organization.certificateFileName)

      if (!result.success) {
        console.error(result.errorMessage)
        return toast.error(result.errorMessage ?? t('common:errors.downloadFile.generic'))
      }
      toast.success(t('common:notifications.downloadSuccess'))
    }
  }, [downloadDocument, session.organization.certificateFileName, t])

  const isAddressUpdateEnabled = useMemo(() => {
    if (doesOrganizationRequiresLocalAddress) {
      const isLocalAddressValid = localAddressSchema.safeParse({
        localStreet: state.localStreet,
        localStreetNumber: state.localStreetNumber,
        country: state.country,
        localPoBox: state.localPoBox,
        localCity: state.localCity,
        localSubDivision: state.localSubDivision,
        localStreet2: state.localStreet2,
        localPostCode: state.localPostCode
      }).success
      const isAddressValid = organizationAddressSchema.safeParse(state).success
      return isLocalAddressValid && isAddressValid
    }
    return organizationAddressSchema.safeParse(state).success
  }, [state, doesOrganizationRequiresLocalAddress])

  const isContactUpdateEnabled = useMemo(() => {
    return organizationContactSchema.safeParse(state).success
  }, [state])

  const isSaveDisabled = useMemo(() => {
    return !isAddressUpdateEnabled || !isContactUpdateEnabled || isFormPristine
  }, [isAddressUpdateEnabled, isContactUpdateEnabled, isFormPristine])

  return (
    <>
      <Container>
        <header className="flex justify-between items-center">
          <H2>{t('settings:organizationDetails.title')}</H2>
        </header>
        <Alert type="info">{t('settings:organizationDetails.alert')}</Alert>
        <Card>
          <CardHeader className="flex justify-between">
            <div className="flex items-center gap-2">
              <InfoFilledIcon className="size-6" />
              <CardTitle title={t('settings:organizationDetails.organizationInfo.title')} />
            </div>
            <Button sizeType="sm" onClick={openUpdateBusinessCertificateDialog}>
              {t('settings:organizationDetails.organizationInfo.actions.updateBusinessCertificate')}
            </Button>
          </CardHeader>
          <CardBody>
            <OrganizationInfoData
              name={organization.name}
              localName={organization.localName}
              placeOfRegistration={organization.placeOfRegistration ?? ''}
              doesOrganizationRequiresLocalAddress={doesOrganizationRequiresLocalAddress}
              businessRegistrationNumber={organization.businessRegistrationNumber ?? ''}
              hasCertificate={!!organization.certificateFileName}
              onClickCertificate={onClickCertificate}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MapPinIcon className="size-6" />
              <CardTitle title={t('settings:organizationDetails.details.address')} />
            </div>
          </CardHeader>
          {doesOrganizationRequiresLocalAddress ? (
            <OrganizationWithLocalAddressForm
              initialState={{
                street: state.street,
                streetNumber: state.streetNumber,
                country: state.country,
                poBox: state.poBox,
                city: state.city,
                subDivision: state.subDivision,
                street2: state.street2,
                postCode: state.postCode,
                localStreet: state.localStreet,
                localStreetNumber: state.localStreetNumber,
                localSubDivision: state.localSubDivision,
                localPoBox: state.localPoBox,
                localCity: state.localCity,
                localPostCode: state.localPostCode,
                localStreet2: state.localStreet2
              }}
              onChange={onAddressWithLocalChange}
            />
          ) : (
            <OrganizationAddressForm
              initialState={{
                street: state.street,
                streetNumber: state.streetNumber,
                country: state.country,
                poBox: state.poBox,
                city: state.city,
                subDivision: state.subDivision,
                street2: state.street2,
                postCode: state.postCode
              }}
              onChange={onAddressChange}
            />
          )}
        </Card>

        <Card>
          <CardHeader className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <PencilIcon className="size-6" />
              <CardTitle title={t('settings:organizationDetails.contact.title')} />
            </div>
          </CardHeader>
          <OrganizationContactForm
            initialState={{
              contactEmail: state.contactEmail,
              contactPhoneNumber: state.contactPhoneNumber,
              contactName: state.contactName
            }}
            onChange={onContactChange}
          />
        </Card>
      </Container>
      <footer className="flex justify-end sticky bottom-0 bg-white p-2.5 border-y">
        <Button
          sizeType="sm"
          className="w-48"
          onClick={onSaveChanges}
          disabled={isSaving || isSaveDisabled}
          isLoading={isSaving}
        >
          {t('common:actions.save')}
        </Button>
      </footer>
      <UpdateBusinessCertificateDialog
        isOpen={isOpenUpdateBusinessCertificateDialog}
        close={closeUpdateBusinessCertificateDialog}
        initialState={{
          businessRegistrationNumber: organization.businessRegistrationNumber ?? '',
          certificateFileName: organization.certificateFileName ?? ''
        }}
      />
    </>
  )
}
